import type { LucideIcon } from 'lucide-react'

/**
 * O contrato de mapa que o [[AbasDeTela]] renderiza, e o par puro que o traduz de e
 * para a URL.
 *
 * A URL carrega UM parâmetro, `?secao=<painel>`: como todo id é único no mapa inteiro
 * (seções e blocos incluídos), o painel identifica sozinho a aba e a seção donas. O
 * trio `{ aba, secao, bloco }` continua sendo o estado que o shell consome — só que
 * derivado, nunca serializado. Três chaves na URL eram três coisas a reconciliar por
 * tela, e o link colado ficava três vezes mais longo pra dizer a mesma coisa.
 *
 * Ids são `string` aqui de propósito: cada tela declara a própria união estreita
 * (`type SecaoId = 'capacidades' | …`) no seu `_lib`, e o mapa dela satisfaz este
 * contrato sem precisar propagar três genéricos por toda a árvore de componentes. A
 * tela ganha o type-safety onde ele paga (o `Record` de slots); o shell trabalha com
 * dado.
 */

export interface BlocoDeTela {
  id: string
  rotulo: string
  icone?: LucideIcon
}

export interface SecaoDeTela {
  id: string
  rotulo: string
  icone?: LucideIcon
  /** Terceiro nível. Seção sem `blocos` renderiza o próprio slot. */
  blocos?: readonly BlocoDeTela[]
}

export interface AbaDeTela {
  id: string
  rotulo: string
  icone?: LucideIcon
  secoes: readonly SecaoDeTela[]
}

export interface NavegacaoDeTela {
  aba: string
  secao: string
  /** `null` quando a seção ativa não tem terceiro nível. */
  bloco: string | null
}

/** A chave de query. Uma só — ver o cabeçalho do módulo. */
export const PARAM_SECAO = 'secao'

/** O mínimo que o parse precisa — serve `URLSearchParams` e o `ReadonlyURLSearchParams` do Next. */
export interface LeitorDeQuery {
  get(name: string): string | null
}

/** As seções de uma aba. Aba inexistente → `[]` (a URL é crua). */
export function secoesDaAba(abas: readonly AbaDeTela[], aba: string): readonly SecaoDeTela[] {
  return abas.find((a) => a.id === aba)?.secoes ?? []
}

/** Os blocos de uma seção; `[]` quando ela não tem terceiro nível. */
export function blocosDaSecao(abas: readonly AbaDeTela[], secao: string): readonly BlocoDeTela[] {
  for (const aba of abas) {
    const def = aba.secoes.find((s) => s.id === secao)
    if (def) return def.blocos ?? []
  }
  return []
}

/** O primeiro caminho completo de uma aba — o destino do link da régua. */
export function primeiroCaminho(abas: readonly AbaDeTela[], aba: string): NavegacaoDeTela | null {
  const secao = secoesDaAba(abas, aba)[0]
  if (!secao) return null
  return { aba, secao: secao.id, bloco: secao.blocos?.[0]?.id ?? null }
}

/** O painel efetivamente mostrado: o bloco quando há terceiro nível, senão a seção. */
export function painelAtivo(nav: NavegacaoDeTela): string {
  return nav.bloco ?? nav.secao
}

/**
 * A aba desenha a sidebar de seções? Uma seção-folha só não é escolha — a sidebar seria um
 * item solto apontando pra onde o usuário já está. Com blocos ela fica: o terceiro nível só
 * existe nela. Exportada porque quem se alinha à coluna do painel (ex.: a barra de alterações
 * do detalhe de usuário) precisa da MESMA regra do shell.
 */
export function abaTemSidebar(aba: AbaDeTela): boolean {
  return aba.secoes.length > 1 || aba.secoes.some((s) => s.blocos)
}

/**
 * O caminho completo de um id de PAINEL dentro de UMA aba — o inverso de `painelAtivo`.
 * `null` para id que não existe nela. Id de seção com blocos cai no primeiro bloco.
 */
export function caminhoDoPainel(aba: AbaDeTela, painel: string): NavegacaoDeTela | null {
  for (const secao of aba.secoes) {
    if (secao.id === painel) return { aba: aba.id, secao: secao.id, bloco: secao.blocos?.[0]?.id ?? null }
    const bloco = secao.blocos?.find((b) => b.id === painel)
    if (bloco) return { aba: aba.id, secao: secao.id, bloco: bloco.id }
  }
  return null
}

/** `caminhoDoPainel` sobre o mapa inteiro. É o que faz `?secao=` bastar. */
export function localizarPainel(abas: readonly AbaDeTela[], painel: string): NavegacaoDeTela | null {
  for (const aba of abas) {
    const caminho = caminhoDoPainel(aba, painel)
    if (caminho) return caminho
  }
  return null
}

/**
 * Lê `?secao=` sempre devolvendo um caminho COERENTE com o mapa recebido.
 *
 * O mapa vem JÁ RECORTADO pelas permissions da tela — é isso que faz a queda de
 * `?secao=<gateada>` funcionar sem o shell saber o que é uma permission: a seção
 * simplesmente não está no mapa, e cai como qualquer id desconhecido.
 *
 * A queda é silenciosa de propósito (URL é entrada de usuário; erro aqui não merece
 * tela de erro): id lixo, gateado ou ausente → primeiro caminho da primeira aba.
 */
export function parseNavegacao(sp: LeitorDeQuery, abas: readonly AbaDeTela[]): NavegacaoDeTela {
  const pedido = sp.get(PARAM_SECAO)
  const achado = pedido ? localizarPainel(abas, pedido) : null
  if (achado) return achado
  const primeira = abas[0]
  return (primeira && primeiroCaminho(abas, primeira.id)) ?? { aba: primeira?.id ?? '', secao: '', bloco: null }
}

/** O inverso do parse: só o painel ativo vai pra URL. */
export function serializeNavegacao(nav: NavegacaoDeTela): URLSearchParams {
  const sp = new URLSearchParams()
  sp.set(PARAM_SECAO, painelAtivo(nav))
  return sp
}

/**
 * O link colável de um painel. Constrói a query do zero: tela que tenha OUTROS
 * parâmetros mescla com `hrefDoPainelPreservando` (detalhe do disparo e do usuário).
 */
export function hrefDoPainel(pathname: string, painel: string): string {
  const sp = new URLSearchParams()
  sp.set(PARAM_SECAO, painel)
  return `${pathname}?${sp.toString()}`
}

/** O mínimo pra mesclar: só saber se serializar. Serve o `ReadonlyURLSearchParams` do Next. */
export interface SerializavelEmQuery {
  toString(): string
}

/**
 * O link colável de um painel PRESERVANDO os demais parâmetros da URL atual.
 *
 * `hrefDoPainel` monta a query do zero, e por isso trocar de seção numa tela que
 * carrega outro param (o `?node_id` do detalhe do disparo) apagava o param junto.
 * Aqui `secao` é SOBRESCRITA (`set`, não `append`): valor repetido na URL colada
 * colapsa em um só, senão a próxima leitura veria dois.
 */
export function hrefDoPainelPreservando(
  pathname: string,
  painel: string,
  atuais: SerializavelEmQuery,
): string {
  const sp = new URLSearchParams(atuais.toString())
  sp.set(PARAM_SECAO, painel)
  return `${pathname}?${sp.toString()}`
}
